import { create } from 'zustand';
import { apiClient } from '@services/api';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { 
  translations as localTranslations, 
  getLocalTranslation,
  getLanguageDirection,
  DEFAULT_LANGUAGE,
} from '../i18n';

interface TranslationBundle {
  translations: Record<string, string>;
  version: string;
  fetchedAt: number;
}

interface TranslationDB extends DBSchema {
  bundles: {
    key: string; // e.g., "metro-herzliya:breakfast:he"
    value: TranslationBundle;
  };
}

// Debug mode - set to true to see missing translation warnings
const DEBUG_MODE = import.meta.env.DEV;

// Track missing keys to avoid duplicate warnings
const missingKeyWarnings = new Set<string>();

interface TranslationState {
  translations: Record<string, string>;
  language: string;
  direction: 'ltr' | 'rtl';
  isLoading: boolean;
  error: string | null;
  
  loadTranslations: (hotelSlug: string, module: string, language: string, forceRefresh?: boolean) => Promise<void>;
  t: (key: string, params?: Record<string, string | number>) => string;
  setLanguage: (language: string) => void;
  clearCache: () => Promise<void>;
}

let db: IDBPDatabase<TranslationDB> | null = null;

async function getDb() {
  if (!db) {
    db = await openDB<TranslationDB>('gcxp-translations', 1, {
      upgrade(db) {
        db.createObjectStore('bundles');
      },
    });
  }
  return db;
}

// Get initial language from localStorage to prevent flash of wrong language
function getInitialLanguage(): string {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('gcxp_language');
    if (stored) return stored;
  }
  return DEFAULT_LANGUAGE;
}

const initialLanguage = getInitialLanguage();

export const useTranslationStore = create<TranslationState>((set, get) => ({
  translations: localTranslations[initialLanguage] || localTranslations[DEFAULT_LANGUAGE] || {},
  language: initialLanguage,
  direction: getLanguageDirection(initialLanguage),
  isLoading: true,
  error: null,

  loadTranslations: async (hotelSlug: string, module: string, language: string, forceRefresh: boolean = false) => {
    // Set direction immediately based on language
    const direction = getLanguageDirection(language);
    set({ isLoading: true, error: null, direction });
    
    // Update document direction
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
    
    const cacheKey = `${hotelSlug}:${module}:${language}`;
    
    // Start with local translations as base
    const baseTranslations = localTranslations[language] || localTranslations['en'] || {};
    
    try {
      // Try to load from IndexedDB first (unless forceRefresh)
      const db = await getDb();
      const cached = !forceRefresh ? await db.get('bundles', cacheKey) : null;
      
      // Use cached if less than 5 minutes old
      const maxAge = 5 * 60 * 1000;
      if (cached && Date.now() - cached.fetchedAt < maxAge) {
        // Merge: local translations as base, then cached (server) translations on top
        const mergedTranslations = { ...baseTranslations, ...cached.translations };
        set({
          translations: mergedTranslations,
          language,
          isLoading: false,
        });
        
        // Refresh in background
        refreshTranslations(hotelSlug, module, language, cached.version);
        return;
      }
      
      // Fetch from server
      const response = await apiClient.get(
        `/api/v1/${hotelSlug}/${module}/translations/${language}`
      );
      
      const bundle: TranslationBundle = {
        translations: response.data.translations,
        version: response.data.version,
        fetchedAt: Date.now(),
      };
      
      // Store in IndexedDB
      await db.put('bundles', bundle, cacheKey);
      
      // Merge: local translations as base, then server translations on top
      const mergedTranslations = { ...baseTranslations, ...bundle.translations };
      set({
        translations: mergedTranslations,
        language,
        isLoading: false,
      });
    } catch (error: unknown) {
      // If fetch fails but we have cached data, use it
      const db = await getDb();
      const cached = await db.get('bundles', cacheKey);

      if (cached) {
        const mergedTranslations = { ...baseTranslations, ...cached.translations };
        set({
          translations: mergedTranslations,
          language,
          isLoading: false,
        });
      } else {
        // Use local translations as fallback
        set({
          translations: baseTranslations,
          language,
          isLoading: false,
          error: null, // Don't show error if we have local translations
        });
      }
    }
  },

  t: (key: string, params?: Record<string, string | number>) => {
    const { translations, language } = get();
    
    // Try loaded translations first
    let text: string | null | undefined = translations[key];
    
    // Fallback to local translations if not found
    if (!text) {
      text = getLocalTranslation(key, language);
    }
    
    // If still not found, use key as fallback
    if (!text) {
      // Log warning in debug mode (only once per key)
      if (DEBUG_MODE && !missingKeyWarnings.has(key)) {
        console.warn(`[i18n] Missing translation key: "${key}" for language: "${language}"`);
        missingKeyWarnings.add(key);
      }
      text = key;
    }
    
    // Replace parameters like {{name}} with values
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = (text as string).replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), String(value));
      });
    }
    
    return text;
  },

  setLanguage: (language: string) => {
    const direction = getLanguageDirection(language);
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
    set({ language, direction });
  },

  clearCache: async () => {
    try {
      const db = await getDb();
      await db.clear('bundles');
    } catch (error) {
      console.error('Failed to clear translation cache:', error);
    }
  },
}));

// Background refresh function
async function refreshTranslations(
  hotelSlug: string,
  module: string,
  language: string,
  currentVersion: string
) {
  try {
    const response = await apiClient.get(
      `/api/v1/${hotelSlug}/${module}/translations/${language}`,
      {
        headers: {
          'If-None-Match': `"${hotelSlug}-${module}-${language}-${currentVersion}"`,
        },
        validateStatus: (status) => status === 200 || status === 304,
      }
    );
    
    if (response.status === 200) {
      const cacheKey = `${hotelSlug}:${module}:${language}`;
      const db = await getDb();
      
      const bundle: TranslationBundle = {
        translations: response.data.translations,
        version: response.data.version,
        fetchedAt: Date.now(),
      };
      
      await db.put('bundles', bundle, cacheKey);
      
      // Update store if this is still the current language
      const state = useTranslationStore.getState();
      if (state.language === language) {
        // Merge with local translations
        const baseTranslations = localTranslations[language] || localTranslations['en'] || {};
        const mergedTranslations = { ...baseTranslations, ...bundle.translations };
        useTranslationStore.setState({ translations: mergedTranslations });
      }
    }
  } catch {
    // Ignore background refresh errors
  }
}

/**
 * Hook for easier access to translation functions
 * Provides t, language, direction, setLanguage, and clearCache
 */
export function useTranslation() {
  const { t, language, direction, setLanguage, isLoading, clearCache } = useTranslationStore();
  return { t, language, direction, setLanguage, isLoading, clearCache };
}

/**
 * Get all missing translation keys for debugging
 */
export function getMissingKeys(): string[] {
  return Array.from(missingKeyWarnings);
}

/**
 * Clear missing key warnings (useful for testing)
 */
export function clearMissingKeyWarnings(): void {
  missingKeyWarnings.clear();
}

// Re-export useful items from translations
export { supportedLanguages, getLanguageDirection } from '../i18n';
